import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FlaskConical, LayoutDashboard, Loader2, X } from "lucide-react";
import Field from "@/shared/components/field";
import DynamicJsonForm from "@/shared/components/DynamicJsonForm";
import DataPathTree from "@/features/automations/components/canvas/DataPathTree";
import type AutomationSummary from "@/features/automations/models/AutomationSummary";
import IntegrationProvidesService from "@/features/integrations_provider/services/integrationProvidesService";
import type IntegrationProvider from "@/features/integrations_provider/models/integrationProvider";
import DashboardService from "../services/DashboardService";
import {
  EXECUTION_METRIC_LABELS,
  WIDGET_TYPE_LABELS,
  type ExecutionMetric,
  type WidgetAggregate,
  type WidgetSpec,
  type WidgetType,
} from "../models/Widget";
import type { WidgetDataResult } from "../models/WidgetData";

interface ManifestOperation {
  id: string;
  displayName?: string;
  parametersSchema?: string;
}

function parseOperations(provider: IntegrationProvider | undefined): ManifestOperation[] {
  if (!provider?.capabilitiesManifest) return [];
  try {
    const manifest = JSON.parse(provider.capabilitiesManifest) as {
      operations?: ManifestOperation[];
    };
    return manifest.operations ?? [];
  } catch {
    return [];
  }
}

export interface WidgetEditorModalProps {
  workspaceId: string;
  /**
   * Automatizaciones archivadas en el workspace. Una fuente `executions` puede limitarse a una de
   * ellas; el backend rechaza cualquier otra, así que el selector solo ofrece estas.
   */
  workflows: AutomationSummary[];
  /** Widget a editar, o null para crear uno nuevo. */
  widget: WidgetSpec | null;
  onSave: (widget: WidgetSpec) => void;
  onClose: () => void;
}

export default function WidgetEditorModal({
  workspaceId,
  workflows,
  widget,
  onSave,
  onClose,
}: WidgetEditorModalProps) {
  const dashboardService = useMemo(() => new DashboardService(), []);
  const providersService = useMemo(() => new IntegrationProvidesService(), []);

  const [title, setTitle] = useState(widget?.title ?? "");
  const [type, setType] = useState<WidgetType>(widget?.type ?? "kpi");
  const [sourceKind, setSourceKind] = useState(widget?.source.kind ?? "executions");
  const [w, setW] = useState(widget?.layout.w ?? 1);
  const [h, setH] = useState(widget?.layout.h ?? 1);
  const [refreshSeconds, setRefreshSeconds] = useState(widget?.refreshSeconds ?? 0);

  // Fuente executions
  const [metric, setMetric] = useState<ExecutionMetric>(widget?.source.metric ?? "runsPerDay");
  const [days, setDays] = useState(widget?.source.days ?? 14);
  // "" = todas las automatizaciones del workspace (el agregado que justifica el dashboard acá).
  const [scopeWorkflowId, setScopeWorkflowId] = useState(widget?.source.workflowId ?? "");

  // Fuente integration
  const [providers, setProviders] = useState<IntegrationProvider[]>([]);
  const [providerId, setProviderId] = useState(widget?.source.providerId ?? "");
  const [operationId, setOperationId] = useState(widget?.source.operationId ?? "");
  const [parametersJson, setParametersJson] = useState(
    JSON.stringify(widget?.source.parameters ?? {}),
  );

  // Binding
  const [itemsPath, setItemsPath] = useState(widget?.binding?.itemsPath ?? "");
  const [labelPath, setLabelPath] = useState(widget?.binding?.labelPath ?? "");
  const [valuePath, setValuePath] = useState(widget?.binding?.valuePath ?? "");
  const [aggregate, setAggregate] = useState<WidgetAggregate>(
    widget?.binding?.aggregate ?? "count",
  );
  const [groupByPath, setGroupByPath] = useState(widget?.binding?.groupBy?.path ?? "");
  const [columnsText, setColumnsText] = useState(
    (widget?.binding?.columns ?? []).map((c) => `${c.path}${c.header ? `|${c.header}` : ""}`).join("\n"),
  );

  // Prueba de fuente
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<WidgetDataResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  useEffect(() => {
    if (sourceKind !== "integration") return;
    providersService
      .getAll({ pageNumber: 1, pageSize: 100 })
      .then((page) => setProviders(page.items))
      .catch(() => setProviders([]));
  }, [providersService, sourceKind]);

  const operations = useMemo(
    () => parseOperations(providers.find((p) => p.id === providerId)),
    [providers, providerId],
  );
  const selectedOperation = operations.find((o) => o.id === operationId);

  const buildWidget = (withBinding: boolean): WidgetSpec => {
    let parameters: Record<string, unknown> = {};
    try {
      parameters = JSON.parse(parametersJson || "{}");
    } catch {
      /* el backend valida; un JSON a medio escribir no debe romper la prueba */
    }

    const columns = columnsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [path, header] = line.split("|");
        return { path: path.trim(), header: header?.trim() || null };
      });

    return {
      id: widget?.id ?? crypto.randomUUID(),
      title: title || "Widget",
      type,
      layout: { w, h },
      refreshSeconds: refreshSeconds >= 5 ? refreshSeconds : null,
      source:
        sourceKind === "integration"
          ? { kind: "integration", providerId, operationId, parameters }
          : { kind: "executions", metric, days, workflowId: scopeWorkflowId || null },
      binding:
        sourceKind === "integration" && withBinding
          ? {
              itemsPath: itemsPath || null,
              labelPath: labelPath || null,
              valuePath: valuePath || null,
              aggregate,
              groupBy: groupByPath ? { path: groupByPath, dateBucket: "day" } : null,
              columns: columns.length > 0 ? columns : null,
            }
          : null,
    };
  };

  // Prueba sin binding: el backend devuelve el envelope crudo (kind "raw") para explorar
  // la respuesta con el árbol y elegir las rutas del binding.
  const testSource = async (raw: boolean) => {
    setTesting(true);
    setTestError(null);
    try {
      const response = await dashboardService.getData(workspaceId, {
        draftWidgets: [buildWidget(!raw)],
      });
      const result = response.widgets[0];
      setTestResult(result?.data ?? null);
      setTestError(result?.error ?? null);
    } catch (err) {
      setTestResult(null);
      setTestError(err instanceof Error ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  };

  const canSave =
    title.trim() !== "" &&
    (sourceKind === "executions" || (providerId !== "" && operationId !== ""));

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-zinc-200/50 bg-white/90 shadow-2xl backdrop-blur-2xl"
        >
          <div className="flex items-start gap-4 border-b border-black/5 p-6">
            <div className="shrink-0 rounded-2xl bg-accent/10 p-3">
              <LayoutDashboard className="size-6 text-accent" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-bold text-foreground">
                {widget ? "Editar widget" : "Nuevo widget"}
              </h3>
              <p className="mt-0.5 text-sm text-foreground/50">
                Elegí la fuente de datos, probála y ajustá cómo se grafica.
              </p>
            </div>
            <button
              onClick={onClose}
              title="Cerrar"
              className="size-8 shrink-0 cursor-pointer rounded-xl border border-black/5 bg-white/50 flex items-center justify-center text-foreground/40 hover:bg-white hover:text-foreground transition-all"
            >
              <X className="size-4" />
            </button>
          </div>

          <div className="grid flex-1 gap-6 overflow-y-auto p-6 md:grid-cols-2">
            {/* ── Columna izquierda: definición ── */}
            <div className="space-y-4">
              <Field
                label="Título"
                name="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Puntos emitidos"
                required
              />

              <div className="space-y-2">
                <span className="ml-1 text-sm font-bold uppercase tracking-wider text-foreground/60">
                  Tipo
                </span>
                <div className="flex gap-1 rounded-xl border border-black/5 bg-black/5 p-1">
                  {(Object.keys(WIDGET_TYPE_LABELS) as WidgetType[]).map((t) => (
                    <button
                      key={t}
                      onClick={() => setType(t)}
                      className={`flex-1 cursor-pointer rounded-lg px-2 py-1.5 text-xs font-semibold transition-all ${
                        type === t
                          ? "bg-white text-accent shadow-sm"
                          : "text-foreground/50 hover:text-foreground/80"
                      }`}
                    >
                      {WIDGET_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <span className="ml-1 text-sm font-bold uppercase tracking-wider text-foreground/60">
                  Fuente
                </span>
                <div className="flex gap-1 rounded-xl border border-black/5 bg-black/5 p-1">
                  {(
                    [
                      ["executions", "Ejecuciones"],
                      ["integration", "Operación de integración"],
                    ] as const
                  ).map(([kind, label]) => (
                    <button
                      key={kind}
                      onClick={() => setSourceKind(kind)}
                      className={`flex-1 cursor-pointer rounded-lg px-2 py-1.5 text-xs font-semibold transition-all ${
                        sourceKind === kind
                          ? "bg-white text-accent shadow-sm"
                          : "text-foreground/50 hover:text-foreground/80"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {sourceKind === "executions" ? (
                <>
                  <Field
                    label="Métrica"
                    name="metric"
                    type="select"
                    value={metric}
                    onChange={(e) => setMetric(e.target.value as ExecutionMetric)}
                    options={Object.entries(EXECUTION_METRIC_LABELS).map(([value, label]) => ({
                      value,
                      label,
                    }))}
                  />
                  <Field
                    label="Automatización"
                    name="scopeWorkflowId"
                    type="select"
                    value={scopeWorkflowId}
                    onChange={(e) => setScopeWorkflowId(e.target.value)}
                    options={[
                      { value: "", label: "Todas las del workspace" },
                      ...workflows.map((wf) => ({ value: wf.workflowId, label: wf.name })),
                    ]}
                  />
                  <Field
                    label="Ventana (días)"
                    name="days"
                    type="number"
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value) || 14)}
                  />
                </>
              ) : (
                <>
                  <Field
                    label="Integración"
                    name="providerId"
                    type="select"
                    value={providerId}
                    onChange={(e) => {
                      setProviderId(e.target.value);
                      setOperationId("");
                    }}
                    placeholder="Elegí un proveedor"
                    options={providers.map((p) => ({ value: p.id, label: p.name }))}
                  />
                  <Field
                    label="Operación"
                    name="operationId"
                    type="select"
                    value={operationId}
                    onChange={(e) => setOperationId(e.target.value)}
                    placeholder="Elegí una operación"
                    options={operations.map((o) => ({
                      value: o.id,
                      label: o.displayName ?? o.id,
                    }))}
                  />
                  {selectedOperation?.parametersSchema && (
                    <div className="space-y-2">
                      <span className="ml-1 text-sm font-bold uppercase tracking-wider text-foreground/60">
                        Parámetros
                      </span>
                      <DynamicJsonForm
                        schema={selectedOperation.parametersSchema}
                        value={parametersJson}
                        onChange={setParametersJson}
                      />
                    </div>
                  )}
                </>
              )}

              <div className="grid grid-cols-3 gap-3">
                <Field
                  label="Ancho (1-4)"
                  name="w"
                  type="select"
                  value={w}
                  onChange={(e) => setW(Number(e.target.value))}
                  options={[1, 2, 3, 4].map((n) => ({ value: n, label: String(n) }))}
                />
                <Field
                  label="Alto (1-3)"
                  name="h"
                  type="select"
                  value={h}
                  onChange={(e) => setH(Number(e.target.value))}
                  options={[1, 2, 3].map((n) => ({ value: n, label: String(n) }))}
                />
                <Field
                  label="Refresh (seg)"
                  name="refreshSeconds"
                  type="number"
                  value={refreshSeconds}
                  onChange={(e) => setRefreshSeconds(Number(e.target.value) || 0)}
                  placeholder="0 = manual"
                />
              </div>
            </div>

            {/* ── Columna derecha: binding + prueba ── */}
            <div className="space-y-4">
              {sourceKind === "integration" && (
                <>
                  <Field
                    label="Items (ruta del arreglo)"
                    name="itemsPath"
                    value={itemsPath}
                    onChange={(e) => setItemsPath(e.target.value)}
                    placeholder="body.documents"
                  />
                  {type === "table" ? (
                    <div className="space-y-2">
                      <span className="ml-1 text-sm font-bold uppercase tracking-wider text-foreground/60">
                        Columnas (una por línea: ruta|Encabezado)
                      </span>
                      <textarea
                        value={columnsText}
                        onChange={(e) => setColumnsText(e.target.value)}
                        rows={4}
                        placeholder={"name|Cliente\npoints|Puntos"}
                        className="w-full rounded-xl border border-black/5 bg-white/80 px-4 py-3 font-mono text-sm text-foreground shadow-sm transition-all focus:border-accent/30 focus:outline-none focus:ring-4 focus:ring-accent/5"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <Field
                          label="Etiqueta (ruta)"
                          name="labelPath"
                          value={labelPath}
                          onChange={(e) => setLabelPath(e.target.value)}
                          placeholder="name"
                        />
                        <Field
                          label="Valor (ruta)"
                          name="valuePath"
                          value={valuePath}
                          onChange={(e) => setValuePath(e.target.value)}
                          placeholder="points"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field
                          label="Agregación"
                          name="aggregate"
                          type="select"
                          value={aggregate}
                          onChange={(e) => setAggregate(e.target.value as WidgetAggregate)}
                          options={["sum", "count", "avg", "min", "max", "none"].map((a) => ({
                            value: a,
                            label: a,
                          }))}
                        />
                        <Field
                          label="Agrupar por día (ruta)"
                          name="groupByPath"
                          value={groupByPath}
                          onChange={(e) => setGroupByPath(e.target.value)}
                          placeholder="ts"
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="flex gap-2">
                {sourceKind === "integration" && (
                  <button
                    onClick={() => void testSource(true)}
                    disabled={testing || !providerId || !operationId}
                    className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-black/5 bg-white/60 px-3 py-2 text-sm font-semibold text-foreground/70 transition-all hover:bg-white hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
                    title="Ejecuta la operación y muestra la respuesta cruda para elegir rutas"
                  >
                    {testing ? <Loader2 className="size-4 animate-spin" /> : <FlaskConical className="size-4" />}
                    Explorar respuesta
                  </button>
                )}
                <button
                  onClick={() => void testSource(false)}
                  disabled={testing || (sourceKind === "integration" && (!providerId || !operationId))}
                  className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-black/5 bg-white/60 px-3 py-2 text-sm font-semibold text-foreground/70 transition-all hover:bg-white hover:text-accent disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {testing ? <Loader2 className="size-4 animate-spin" /> : <FlaskConical className="size-4" />}
                  Probar widget
                </button>
              </div>

              {testError && (
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-xs font-medium leading-relaxed text-amber-700">
                  {testError}
                </div>
              )}

              {testResult && (
                <div className="max-h-64 overflow-auto rounded-xl border border-black/5 bg-white/60 p-3">
                  {testResult.kind === "raw" ? (
                    <DataPathTree value={testResult.raw} buildCopyText={(p) => p} />
                  ) : (
                    <pre className="whitespace-pre-wrap break-all font-mono text-xs text-foreground/70">
                      {JSON.stringify(testResult, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-black/5 p-5">
            <button
              onClick={onClose}
              className="cursor-pointer rounded-xl border border-black/5 bg-white/60 px-5 py-2 text-sm font-semibold text-foreground/70 transition-all hover:bg-white"
            >
              Cancelar
            </button>
            <button
              onClick={() => onSave(buildWidget(true))}
              disabled={!canSave}
              className="cursor-pointer rounded-xl bg-accent px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {widget ? "Aplicar cambios" : "Añadir widget"}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
